import type { ProtectedActionChildRunApproval } from '@heidi/agents/contracts';
import { useCallback, type ReactNode } from 'react';
import { useMutateResolveApproval } from '../agent-harness/hooks';
import { ApprovalInputDetails } from '../inline-approval/approval-input-details';
import type { DraftApprovalBodyRenderArgs } from '../inline-approval/draft-body';
import type {
  ApprovalRequestBlockProps,
  ApprovalRequestInput,
} from '../projects/components/ApprovalRequestBlock';
import { ApprovalRequestBlock } from '../projects/components/ApprovalRequestBlock';

import { getWorkHostPort } from '../work-home/work-home-ports';
import { desktopControlApprovalPort } from './desktop-control-approval';

type ProtectedActionApprovalProps = {
  approval: ProtectedActionChildRunApproval;
  patientContext: ApprovalRequestBlockProps['patientContext'];
  renderDraftBody: (args: DraftApprovalBodyRenderArgs) => ReactNode;
};

/**
 * Resolves against the child run: the parent chat's resolver is parent-scoped.
 * Throwing on failure lets the card unlatch and show its own toast.
 */
export const ProtectedActionApproval = ({
  approval,
  patientContext,
  renderDraftBody,
}: ProtectedActionApprovalProps) => {
  const { mutateAsync } = useMutateResolveApproval();
  const isNangoIntegrationsEnabled =
    getWorkHostPort('shell').useIsNangoIntegrationsEnabled();
  const { agentRunId } = approval;
  const renderInputDetails = useCallback(
    (input: ApprovalRequestInput) => (
      <ApprovalInputDetails approval={input} renderDraftBody={renderDraftBody} />
    ),
    [renderDraftBody]
  );
  const useAskHeidiApprovalGuard = useCallback(
    () => getWorkHostPort('desktop').useTemplateCreationApprovalGuard(),
    []
  );

  const onResolve = useCallback<ApprovalRequestBlockProps['onResolve']>(
    async (approvalId, verdict, reason) => {
      const response = await mutateAsync({
        agentRunId,
        approvalId,
        body: { verdict, reason, modified_input: null },
      });
      if (!response.accepted) throw new Error('Approval was not accepted');
    },
    [agentRunId, mutateAsync]
  );

  return (
    <ApprovalRequestBlock
      agentRunId={agentRunId}
      approval={{
        approval_id: approval.approvalId,
        tool_name: approval.toolName,
        tool_input: approval.toolInput,
        tool_call_id: approval.toolCallId,
        toolCallDisplay: approval.toolCallDisplay,
        context: approval.context,
      }}
      onResolve={onResolve}
      isNangoIntegrationsEnabled={isNangoIntegrationsEnabled}
      desktopControl={desktopControlApprovalPort}
      useApprovalGuard={useAskHeidiApprovalGuard}
      renderInputDetails={renderInputDetails}
      patientContext={patientContext}
    />
  );
};
